import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  inject,
} from '@angular/core';
import {
  TaskCompletedStatus,
  TaskItemStatus,
} from '@r1-development/resolution-mechanicalturk-api-client';
import { getDateInLocalTimezone } from '../../utils/date.utils';
import { MatTooltip } from '@angular/material/tooltip';

interface LabelInfo {
  style: 'negative' | 'neutral' | 'completed' | 'denied' | 'none';
  label: string;
}

@Component({
  selector: 'ri-resolution-ui-task-date-label',
  template: `
    @switch (dateLabelInfo().style) {
      @case ('negative') {
        <div class="rounded-[8px] bg-red-100 p-1 px-2 text-red-900">
          {{ dateLabelInfo().label }}
        </div>
      }
      @case ('neutral') {
        <div class="rounded-[8px] bg-orange-100 p-1 px-2 text-orange-900">
          {{ dateLabelInfo().label }}
        </div>
      }
      @case ('completed') {
        <div class="rounded-[8px] bg-green-100 p-1 px-2 text-green-900">
          <i
            style="font-size: 0.85rem"
            class="r1-icon r1-icon-check-light font-bold"
            matTooltip="Completed"
            matTooltipShowDelay="200"
            matTooltipPosition="right"
          ></i>
          {{ dateLabelInfo().label }}
        </div>
      }
      @case ('denied') {
        <div class="rounded-[8px] bg-red-100 p-1 px-2 text-red-900">
          <i
            style="font-size: 0.85rem"
            class="r1-icon r1-icon-disable-light font-bold"
            matTooltip="Denied"
            matTooltipShowDelay="200"
            matTooltipPosition="right"
          ></i>
          {{ dateLabelInfo().label }}
        </div>
      }
      @case ('none') {
        <div>{{ dateLabelInfo().label }}</div>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  imports: [MatTooltip],
})
export class TaskDateLabelComponent {
  private readonly datePipe = inject(DatePipe);

  readonly task = input.required<{
    dueDate: string;
    completedAt: string;
    taskStatus: TaskItemStatus;
    work?: { completedStatus?: TaskCompletedStatus };
  }>();
  readonly taskItemStatus = TaskItemStatus;

  readonly dateLabelInfo = computed<LabelInfo>(() => {
    const task = this.task();

    const dueTime = getDateInLocalTimezone(task.dueDate)?.getTime();
    const dateDiff = this.getDateDiffInDays(task.dueDate);

    if (this.isCompleted(task)) {
      return {
        style: 'completed',
        label: this.datePipe.transform(task.completedAt, 'mediumDate') || '',
      };
    }

    if (this.isDenied(task)) {
      return {
        style: 'denied',
        label: this.datePipe.transform(task.completedAt, 'mediumDate') || '',
      };
    }

    // Otherwise it is in-progress task
    const minDateWithoutTz = '0001-01-01T00:00:00';
    if (
      task.dueDate.substring(0, minDateWithoutTz.length) === minDateWithoutTz
    ) {
      return {
        style: 'negative',
        label: `DUE DATE UNKNOWN`,
      };
    } else if (dateDiff > 0) {
      return {
        style: 'negative',
        label: `${dateDiff} ${dateDiff > 1 ? 'DAYS' : 'DAY'} OVERDUE`,
      };
    } else if (dateDiff === 0) {
      return {
        style: 'neutral',
        label: 'DUE TODAY',
      };
    }
    return {
      style: 'none',
      label: this.datePipe.transform(dueTime, 'mediumDate') || '',
    };
  });

  constructor() {}

  private isCompleted(task: {
    taskStatus: TaskItemStatus;
    work?: { completedStatus?: TaskCompletedStatus };
  }): boolean {
    return (
      task.taskStatus === TaskItemStatus.Completed &&
      task.work?.completedStatus !== TaskCompletedStatus.Denied
    );
  }

  private isDenied(task: {
    taskStatus: TaskItemStatus;
    work?: { completedStatus?: TaskCompletedStatus };
  }): boolean {
    return (
      task.taskStatus === TaskItemStatus.Completed &&
      task.work?.completedStatus === TaskCompletedStatus.Denied
    );
  }

  private getDateDiffInDays(taskDueDate: string): number {
    const dueDate = taskDueDate
      ? getDateInLocalTimezone(taskDueDate)
      : new Date();
    dueDate.setHours(0, 0, 0, 0);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    const timeDiff = todayDate.getTime() - dueDate.getTime();
    const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return diffDays;
  }
}
